import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr, API } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import { Field, EmptyYacht } from "./Inventory";
import { Plus, Trash2, Users as UsersIcon, ShieldCheck, UserPlus, Search, Briefcase, IdCard, AlertTriangle, Wallet, Lock, LockOpen, MessageSquare } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { toast } from "sonner";

// Hierarchy mirrors the backend
const RANK = {
  owner: 100,
  manager: 75,
  captain: 50,
  first_officer: 40,
  engineer: 20,
  accountant: 20,
  deckhand: 10,
  stewardess: 10,
  chef: 10,
  hostess: 10,
  cleaner: 10,
  detailer: 10,
  delivery_crew: 10,
  night_watch: 10,
  maintenance_helper: 10,
  provider: 10,
};

const ROLE_LABELS = {
  owner: "Owner",
  manager: "Manager",
  captain: "Captain",
  first_officer: "First Officer",
  engineer: "Engineer",
  accountant: "Accountant",
  deckhand: "Deckhand",
  stewardess: "Stewardess",
  chef: "Chef",
  hostess: "Hostess",
  cleaner: "Cleaner",
  detailer: "Detailer",
  delivery_crew: "Delivery Crew",
  night_watch: "Night Watch",
  maintenance_helper: "Maintenance Helper",
  provider: "Online Supplier",
};

export default function UsersPage() {
  const { t } = useTranslation();
  const { activeTour } = useTour();
  const { activeYacht } = useYacht();
  const { user } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Direct message a supplier contact: get-or-create the DM thread, then
  // jump to the Messages page with it open.
  const openDM = async (m) => {
    try {
      const { data } = await api.post("/direct-threads", { user_id: m.user_id, yacht_id: activeYacht.id });
      navigate(`/app/messages?dm=${data.id}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [members, setMembers] = useState([]);
  // HR alert chips (expired/expiring certs, payroll to confirm) per user_id.
  const [teamAlerts, setTeamAlerts] = useState({});
  useEffect(() => {
    api.get("/crew/team-alerts").then(({ data }) => setTeamAlerts(data || {})).catch(() => setTeamAlerts({}));
  }, []);
  const [exCrew, setExCrew] = useState([]);
  const [toPurge, setToPurge] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("engineer");

  // "Add existing" tab state
  const [tab, setTab] = useState("invite");
  const [known, setKnown] = useState([]);
  const [knownQuery, setKnownQuery] = useState("");
  const [pickedUser, setPickedUser] = useState(null);
  const [pickedRole, setPickedRole] = useState("engineer");
  const [knownBusy, setKnownBusy] = useState(false);

  const myRole = activeYacht?.my_role || "deckhand";
  const myRank = RANK[myRole] ?? 0;

  // ── Crew access locking (Owner/Manager) ──────────────────────────────
  const [crewLock, setCrewLock] = useState(false);      // yacht-wide switch
  const [crewLockConfirm, setCrewLockConfirm] = useState(null); // {enable}
  const [toLock, setToLock] = useState(null);           // member to lock
  const [lockBusy, setLockBusy] = useState(false);
  const isLockAdmin = myRole === "owner" || myRole === "manager";

  const canLock = (m) =>
    isLockAdmin &&
    m.user_id &&
    !m.pending &&
    m.user_id !== user?.id &&
    m.role !== "owner" &&
    m.role !== "provider" &&
    (myRole === "owner" || (RANK[m.role] ?? 0) < RANK.manager);

  const setMemberLock = async (m, locked) => {
    setLockBusy(true);
    try {
      await api.post(`/yacht-members/${m.id}/lock`, { locked });
      toast.success(locked
        ? `${m.name || m.email} can no longer access this yacht.`
        : `${m.name || m.email}'s access has been restored.`);
      setToLock(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setLockBusy(false); }
  };

  const applyCrewLock = async (enable) => {
    setLockBusy(true);
    try {
      await api.post(`/yachts/${activeYacht.id}/crew-lock`, { enabled: enable });
      setCrewLock(enable);
      toast.success(enable
        ? "All crew access is now locked. Owners and Managers keep full access."
        : "Crew access has been restored.");
      setCrewLockConfirm(null);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLockBusy(false); }
  };

  // Compute the roles the current user is allowed to invite.
  const ownerMemberExists = useMemo(
    () => members.some((m) => m.id !== "owner" && m.role === "owner"),
    [members],
  );
  const managerMemberExists = useMemo(
    () => members.some((m) => m.role === "manager"),
    [members],
  );
  const allowedRoles = useMemo(() => {
    // Operational roles a captain (and above) may always invite, in display
    // order: First Officer → senior crew → deckhand → service crew → supplier.
    const OPS = [
      "first_officer", "engineer", "accountant", "deckhand", "stewardess",
      "chef", "hostess", "cleaner", "detailer", "delivery_crew",
      "night_watch", "maintenance_helper", "provider",
    ];
    if (myRole === "owner") {
      return ["owner", "manager", "captain", ...OPS];
    }
    if (myRole === "manager") {
      const list = ["captain", ...OPS];
      if (!ownerMemberExists) list.unshift("owner");
      return list;
    }
    if (myRole === "captain") {
      const list = [...OPS];
      if (!managerMemberExists) list.unshift("manager");
      if (!ownerMemberExists) list.unshift("owner");
      return list;
    }
    if (myRole === "first_officer") {
      // "Almost captain": can invite any role below captain, but not a senior.
      return OPS.filter((r) => r !== "first_officer");
    }
    return [];
  }, [myRole, ownerMemberExists, managerMemberExists]);

  const canInvite = allowedRoles.length > 0;

  useEffect(() => {
    if (!allowedRoles.includes(role)) setRole(allowedRoles[0] || "");
    if (!allowedRoles.includes(pickedRole)) setPickedRole(allowedRoles[0] || "");
  }, [allowedRoles, role, pickedRole]);

  const load = async () => {
    if (!activeYacht?.id) return;
    const { data } = await api.get(`/yachts/${activeYacht.id}/members`);
    api.get(`/yachts/${activeYacht.id}`).then((r) => setCrewLock(!!r.data?.crew_lock)).catch(() => {});
    const sorted = [...data].sort((a, b) => {
      // Service providers always sort to the bottom; within-bucket
      // ordering = hierarchy rank desc, then alphabetical by name/email.
      const aProvider = a.role === "provider";
      const bProvider = b.role === "provider";
      if (aProvider !== bProvider) return aProvider ? 1 : -1;
      const ra = RANK[a.role] ?? 0;
      const rb = RANK[b.role] ?? 0;
      if (rb !== ra) return rb - ra;
      return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    });
    setMembers(sorted);
  };

  const loadExCrew = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/yachts/${activeYacht.id}/ex-crew`);
      setExCrew(data || []);
    } catch { setExCrew([]); }
  };

  // Re-invite an ex-crew member back via the standard email invite flow. On
  // acceptance their retained HR data automatically reconnects.
  const reinviteExCrew = async (x) => {
    try {
      await api.post("/users/invite", {
        yacht_id: activeYacht.id, email: x.email, role: x.last_role || "deckhand",
      });
      toast.success(`Invite sent to ${x.name || x.email}`);
      await load(); await loadExCrew();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const purgeExCrewHr = async () => {
    if (!toPurge) return;
    try {
      await api.delete(`/crew/${toPurge.user_id}/hr`, { params: { yacht_id: activeYacht.id } });
      toast.success("HR data permanently deleted");
      setToPurge(null);
      await loadExCrew();
    } catch (e) { toast.error(fmtErr(e)); setToPurge(null); }
  };

  // Offline → Linked transition banners. Server stores one notification
  // per offline-supplier-acceptance for each senior who invited them;
  // we surface them here as a one-shot dismissable banner above the
  // section list, plus a single toast on first mount.
  const [supplierNotifs, setSupplierNotifs] = useState([]);
  const loadSupplierNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      const fresh = (data || []).filter((n) => n.type === "supplier_linked");
      setSupplierNotifs(fresh);
      // Single aggregated toast on first observation. Repeated mounts
      // won't re-toast — we only fire when the count increases.
      if (fresh.length > 0) {
        const seenKey = `seavanto:supplier_linked_toast:${fresh.map((n) => n.id).sort().join(",")}`;
        if (!sessionStorage.getItem(seenKey)) {
          sessionStorage.setItem(seenKey, "1");
          const msg = fresh.length === 1
            ? fresh[0].message
            : `${fresh.length} suppliers just became linked Service Providers.`;
          toast.success(msg);
        }
      }
    } catch { /* non-fatal */ }
  };
  const dismissSupplierNotif = async (n) => {
    setSupplierNotifs((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.post(`/notifications/${n.id}/dismiss`); } catch { /* swallow */ }
  };

  useEffect(() => {
    setMembers([]);
    load(); loadSupplierNotifs(); loadExCrew();
    /* eslint-disable-next-line */
  }, [activeYacht?.id]);

  const loadKnown = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/users/known`, { params: { yacht_id: activeYacht.id } });
      setKnown(data || []);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    if (open && tab === "existing") {
      setPickedUser(null);
      setKnownQuery("");
      loadKnown();
    }
    // eslint-disable-next-line
  }, [open, tab, activeYacht?.id]);

  const invite = async () => {
    if ((email || "").trim().toLowerCase() === (user?.email || "").toLowerCase()) {
      toast.error("You can't invite yourself to your own yacht.");
      return;
    }
    try {
      await api.post("/users/invite", { yacht_id: activeYacht.id, email, role });
      toast.success("Invitation sent");
      setEmail(""); setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addExisting = async () => {
    if (!pickedUser || !pickedRole) return;
    setKnownBusy(true);
    try {
      await api.post("/users/invite", {
        yacht_id: activeYacht.id,
        email: pickedUser.email,
        role: pickedRole,
      });
      toast.success(`${pickedUser.name} added to ${activeYacht.name}`);
      setOpen(false);
      setPickedUser(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setKnownBusy(false);
    }
  };

  const filteredKnown = useMemo(() => {
    const q = knownQuery.trim().toLowerCase();
    if (!q) return known;
    return known.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q),
    );
  }, [known, knownQuery]);

  const canRemove = (m) => {
    if (m.id === "owner") return false;
    // Senior roles can request removal of anyone (including themselves);
    // the backend resolves the approver per the rank matrix.
    return ["owner", "manager", "captain", "first_officer"].includes(myRole);
  };

  // Member rank vs caller rank (used to decorate the confirmation modal).
  // For self-removal this is irrelevant: we use the dedicated `isSelf` branch.
  const ranksAbove = (m) => (RANK[m?.role] ?? 0) > myRank;

  // Mirrors the backend leave-guard: a senior (owner/manager/captain) cannot
  // leave if they're the only confirmed senior on the yacht. Used to disable
  // the Leave button + show a tooltip BEFORE the user opens the confirm dialog.
  const SENIOR = ["owner", "manager", "captain", "first_officer"];
  const otherConfirmedSeniors = useMemo(
    () => members.filter(
      (x) => x.user_id !== user?.id && !x.pending && SENIOR.includes(x.role),
    ),
    [members, user?.id],
  );
  const isSoleSenior = SENIOR.includes(myRole) && otherConfirmedSeniors.length === 0;
  const soleSeniorReason = isSoleSenior
    ? `You're the only ${ROLE_LABELS[myRole]?.toLowerCase() || myRole} on this yacht. Delete the yacht first, or invite another owner/manager/captain before leaving the crew.`
    : "";

  const [toRemove, setToRemove] = useState(null);
  const [pendingByMember, setPendingByMember] = useState({});

  const loadPendingRemovals = async (rows) => {
    const list = rows || members;
    if (!list?.length) { setPendingByMember({}); return; }
    const entries = await Promise.all(list.map(async (m) => {
      try {
        const { data } = await api.get(`/yacht-members/${m.id}/pending-remove`);
        return [m.id, data];
      } catch { return [m.id, null]; }
    }));
    setPendingByMember(Object.fromEntries(entries.filter(([, v]) => v)));
  };
  useEffect(() => { loadPendingRemovals(members); /* eslint-disable-next-line */ }, [members]);

  const confirmRemove = async () => {
    if (!toRemove) return;
    try {
      const { data } = await api.delete(`/yacht-members/${toRemove.id}`);
      const isSelf = toRemove.user_id === user?.id;
      if (data?.cancelled_invite) {
        toast.success(
          `Invitation to ${toRemove.name || toRemove.email} cancelled.`,
        );
      } else if (data?.mode === "approval_pending") {
        toast.success(
          isSelf
            ? `${data.approver?.name || "The senior on this yacht"} will be emailed to approve your departure.`
            : `${toRemove.name || toRemove.email} will be notified to approve their removal.`,
        );
      } else {
        toast.success(
          isSelf
            ? `You have left ${activeYacht.name}.`
            : `${toRemove.name || toRemove.email} removed from crew`,
        );
      }
      setToRemove(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const cancelPendingRemove = async (m) => {
    if (!confirm(`Cancel the pending removal request for ${m.name || m.email}?`)) return;
    try {
      await api.post(`/yacht-members/${m.id}/cancel-remove`);
      toast.success("Removal request cancelled");
      loadPendingRemovals();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("crew.title")}
        right={
          canInvite ? (
            <div className="flex items-center gap-2">
              {["owner", "manager", "captain", "first_officer"].includes(activeYacht.my_role) && (
                <Link to="/app/crew-docs">
                  <Button variant="outline" data-testid="crew-docs-overview-btn">
                    <IdCard className="h-4 w-4 mr-2" />Expiring docs
                  </Button>
                </Link>
              )}
              <TourReplayButton tourId="invite-crew" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-ink hover:bg-black" data-testid="invite-btn">
                  <Plus className="h-4 w-4 mr-2" />Add crew
                </Button>
              </DialogTrigger>
              <DialogContent
                className="max-w-lg"
                onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
                onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
                onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
              >
                <DialogHeader><DialogTitle>Add crew to {activeYacht.name}</DialogTitle></DialogHeader>
                <Tabs value={tab} onValueChange={setTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2" data-tour="crew-invite-tabs">
                    <TabsTrigger value="invite" data-testid="tab-invite-new">Invite new</TabsTrigger>
                    <TabsTrigger value="existing" data-testid="tab-add-existing">Add existing</TabsTrigger>
                  </TabsList>

                  <TabsContent value="invite" className="space-y-3 pt-3">
                    <Field label="Email">
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" data-testid="invite-email" />
                    </Field>
                    <Field label="Role">
                      <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role">
                        {allowedRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                      {myRole === "manager" && (
                        <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          As manager you can invite captains and lower ranks{ownerMemberExists ? "" : ", plus an owner if one isn't on the yacht yet"}.
                        </p>
                      )}
                      {myRole === "captain" && (
                        <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          As captain you can invite engineers and lower ranks{managerMemberExists ? "" : ", plus a manager if one isn't on the yacht yet"}{ownerMemberExists ? "" : ", and an owner if one isn't on the yacht yet"}.
                        </p>
                      )}
                      {myRole === "first_officer" && (
                        <p className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3" />
                          As first officer you can invite crew below captain rank. Ask an owner, manager or captain to add another senior officer.
                        </p>
                      )}
                    </Field>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                      <Button onClick={invite} disabled={!email || !role} className="bg-ink hover:bg-black" data-testid="send-invite">Send invite</Button>
                    </DialogFooter>
                  </TabsContent>

                  <TabsContent value="existing" className="space-y-3 pt-3">
                    <p className="text-xs text-zinc-500">
                      Add a crew member who's already on Seavanto to this yacht. They'll get a notification email — no new password needed.
                    </p>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
                      <Input
                        placeholder="Search by name or email"
                        value={knownQuery}
                        onChange={(e) => setKnownQuery(e.target.value)}
                        className="pl-8"
                        data-testid="known-search"
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto rounded-md border border-zinc-200 divide-y" data-testid="known-list">
                      {filteredKnown.length === 0 && (
                        <div className="p-6 text-center text-sm text-zinc-500">
                          {known.length === 0
                            ? "No existing crew yet. Invite someone new from the other tab."
                            : "No matches."}
                        </div>
                      )}
                      {filteredKnown.map((u) => {
                        const isPicked = pickedUser?.id === u.id;
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => setPickedUser(u)}
                            className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50 ${isPicked ? "bg-zinc-100" : ""}`}
                            data-testid={`pick-known-${u.id}`}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={u.picture} />
                              <AvatarFallback className="bg-zinc-200 text-zinc-700 text-xs">
                                {(u.name || u.email).slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{u.name}</div>
                              <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                              {u.yachts?.length > 0 && (
                                <div className="text-[11px] text-zinc-400 truncate">
                                  On: {u.yachts.join(", ")}
                                </div>
                              )}
                            </div>
                            <UserPlus className={`h-4 w-4 ${isPicked ? "text-ink" : "text-zinc-300"}`} />
                          </button>
                        );
                      })}
                    </div>
                    {pickedUser && (
                      <Field label={`Assign ${pickedUser.name} as`}>
                        <select
                          className="h-9 w-full rounded-md border border-input px-3 text-sm"
                          value={pickedRole}
                          onChange={(e) => setPickedRole(e.target.value)}
                          data-testid="pick-known-role"
                        >
                          {allowedRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      </Field>
                    )}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                      <Button
                        onClick={addExisting}
                        disabled={!pickedUser || !pickedRole || knownBusy}
                        className="bg-ink hover:bg-black"
                        data-testid="add-existing-confirm"
                      >
                        {knownBusy ? "Adding…" : "Add to yacht"}
                      </Button>
                    </DialogFooter>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
            </div>
          ) : null
        }
      />

      {!canInvite && (
        <div className="mt-6 rounded-md border border-zinc-200 bg-white p-4 text-sm text-zinc-600" data-testid="no-invite-permission">
          You don't have permission to invite users on this yacht. Ask the owner or captain.
        </div>
      )}

      {supplierNotifs.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="supplier-linked-banners">
          {supplierNotifs.map((n) => (
            <div
              key={n.id}
              className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3"
              data-testid={`supplier-linked-banner-${n.id}`}
            >
              <UserPlus className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" />
              <div className="flex-1 text-sm text-emerald-900 leading-relaxed">
                {n.message || `${n.supplier_name || "A supplier"} is now a linked Service Provider${n.yacht_name ? " on " + n.yacht_name : ""}.`}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dismissSupplierNotif(n)}
                className="h-7 px-2 text-xs text-emerald-800 hover:bg-emerald-100"
                data-testid={`supplier-linked-banner-dismiss-${n.id}`}
              >Got it</Button>
            </div>
          ))}
        </div>
      )}

      {(() => {
        const crewRows = members.filter((m) => m.role !== "provider");
        const providerRows = members.filter((m) => m.role === "provider");
        const renderRow = (m) => {
          const initials = (m.name || m.email || "U").slice(0, 2).toUpperCase();
          const isSelf = m.user_id === user?.id;
          const removable = canRemove(m);
          const pendingRm = pendingByMember[m.id];
          const supplierInfoClick = m.role === "provider" && m.supplier_card_id
            ? () => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("view", "network"); n.set("supplier", m.supplier_card_id); return n; })
            : null;
          return (
            <li key={m.id + m.user_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`crew-row-${m.id}`}>
              <div
                className={`flex items-start sm:items-center gap-3 min-w-0 flex-1${supplierInfoClick ? " cursor-pointer" : ""}`}
                onClick={supplierInfoClick || undefined}
                title={supplierInfoClick ? "Open supplier info" : undefined}
                data-testid={supplierInfoClick ? `supplier-row-open-${m.id}` : undefined}
              >
                <Avatar className="h-[50px] w-[50px] shrink-0">
                  <AvatarImage src={m.profile_photo ? `${API}/files/${m.profile_photo}` : m.picture} data-testid={`crew-photo-${m.id}`} />
                  <AvatarFallback className="bg-zinc-200 text-zinc-700 text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    <span className="break-words">
                      {m.role === "provider" && m.company
                        ? m.company
                        : (m.name || m.email)}
                    </span>
                    {isSelf && <span className="text-[10px] text-zinc-500 uppercase tracking-wider">you</span>}
                    {m.role === "provider" && m.team_role && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200"
                        data-testid={`team-role-tag-${m.id}`}
                      >
                        {m.team_role === "manager" ? "Account manager" : m.team_role}
                      </span>
                    )}
                    {m.pending && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                        data-testid={`pending-badge-${m.id}`}
                      >
                        Pending invite
                      </span>
                    )}
                  </div>
                  {m.role === "provider" && m.company && m.name && (
                    <div className="text-xs text-zinc-700 truncate" data-testid={`member-contact-${m.id}`}>
                      {m.name}
                    </div>
                  )}
                  <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                  {(m.phone || m.company_phone) && (
                    <div className="text-xs text-zinc-500 truncate" data-testid={`member-phone-${m.id}`}>
                      {m.phone || m.company_phone}
                    </div>
                  )}
                  {m.pending && !isSelf && (
                    <div className="mt-0.5 text-[11px] text-amber-700/80" data-testid={`pending-hint-${m.id}`}>
                      {m.has_account
                        ? "They already have an account — waiting for them to accept the invite."
                        : "Account not confirmed yet — they haven't set a password."}
                    </div>
                  )}
                  {m.role !== "provider" && (() => {
                    const hr = m.user_id ? teamAlerts[m.user_id] : null;
                    if (!hr || (!hr.certs_expired && !hr.certs_expiring && !hr.payroll_pending)) return null;
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`team-alerts-${m.id}`}>
                        {hr.certs_expired > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" data-testid={`team-cert-expired-${m.id}`}>
                            <AlertTriangle className="h-3 w-3" />{hr.certs_expired} cert{hr.certs_expired > 1 ? "s" : ""} expired
                          </span>
                        )}
                        {hr.certs_expiring > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`team-cert-expiring-${m.id}`}>
                            <AlertTriangle className="h-3 w-3" />{hr.certs_expiring} cert{hr.certs_expiring > 1 ? "s" : ""} expiring
                          </span>
                        )}
                        {hr.payroll_pending > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid={`team-payroll-${m.id}`}>
                            <Wallet className="h-3 w-3" />{hr.payroll_pending} to confirm
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {isSelf && isSoleSenior && (
                    <div
                      className="mt-0.5 text-[11px] text-zinc-500"
                      data-testid="sole-senior-hint"
                    >
                      You're the only {ROLE_LABELS[myRole]?.toLowerCase() || myRole} on this yacht — delete the yacht or invite another senior before leaving.
                    </div>
                  )}
                  {pendingRm && (
                    <div className="mt-1 text-[11px] text-amber-800 inline-flex items-center gap-1.5 flex-wrap">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                      {pendingRm.is_self_removal
                        ? `Departure awaiting ${pendingRm.approver_name || "senior"}'s approval`
                        : "Removal awaiting their consent"}
                      <button
                        type="button"
                        onClick={() => cancelPendingRemove(m)}
                        className="underline hover:text-amber-900 ml-1"
                        data-testid={`cancel-remove-${m.id}`}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap pl-12 sm:pl-0 shrink-0">
                {m.role === "provider" && m.user_id && !isSelf && !m.pending && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7"
                    onClick={(e) => { e.stopPropagation(); openDM(m); }}
                    title={`Message ${m.name || m.email}`}
                    data-testid={`dm-btn-${m.id}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />
                    <span className="text-xs">Message</span>
                  </Button>
                )}
                {SENIOR.includes(myRole) && m.user_id && m.role !== "provider" && m.user_id !== user?.id && !ranksAbove(m) && (
                  <Link
                    to={`/app/crew-hr/${m.user_id}?yacht_id=${activeYacht.id}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                    data-testid={`hr-link-${m.id}`}
                  >
                    <IdCard className="h-3 w-3" /> HR record
                  </Link>
                )}
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-zinc-200">{ROLE_LABELS[m.role] || m.role}</span>
                {m.locked && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
                    data-testid={`locked-badge-${m.id}`}
                  >
                    <Lock className="h-3 w-3" /> Access locked
                  </span>
                )}
                {canLock(m) && (
                  m.locked ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMemberLock(m, false)}
                      disabled={lockBusy}
                      title="Restore this member's access"
                      data-testid={`unlock-${m.id}`}
                    >
                      <LockOpen className="h-3.5 w-3.5 mr-1" />
                      <span className="text-xs">Unlock</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToLock(m)}
                      disabled={lockBusy}
                      title="Lock this member's access"
                      data-testid={`lock-${m.id}`}
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                  )
                )}
                {removable && !pendingRm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setToRemove(m)}
                    data-testid={`remove-${m.id}`}
                    disabled={isSelf && isSoleSenior}
                    title={
                      isSelf && isSoleSenior
                        ? soleSeniorReason
                        : m.pending && !isSelf
                          ? "Cancel pending invite"
                          : isSelf
                            ? "Leave this yacht"
                            : "Remove member"
                    }
                  >
                    {m.pending && !isSelf ? (
                      <span className="text-xs">Cancel invite</span>
                    ) : isSelf ? (
                      <span className="text-xs">Leave</span>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            </li>
          );
        };

        return (
          <>
            {/* ───── Crew access lock (Owner/Manager emergency switch) ───── */}
            {isLockAdmin && (
              <div
                className={`mt-8 rounded-md border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                  crewLock ? "border-red-300 bg-red-50" : "border-zinc-200 bg-white"
                }`}
                data-testid="crew-lock-card"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${crewLock ? "bg-red-100" : "bg-zinc-100"}`}>
                    <Lock className={`h-4 w-4 ${crewLock ? "text-red-600" : "text-zinc-500"}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">
                      Crew access lock
                      {crewLock && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="crew-lock-active-badge">Active</span>}
                    </div>
                    <div className="text-xs text-zinc-600 mt-0.5">
                      {crewLock
                        ? "All crew access is locked — the yacht is hidden from everyone below Manager. Owners and Managers keep full access."
                        : "Emergency switch: instantly locks the yacht for everyone below Manager (incl. the Captain). Individual members can also be locked from their row below."}
                    </div>
                  </div>
                </div>
                <Button
                  variant={crewLock ? "outline" : "destructive"}
                  size="sm"
                  onClick={() => setCrewLockConfirm({ enable: !crewLock })}
                  disabled={lockBusy}
                  data-testid="crew-lock-toggle-btn"
                >
                  {crewLock ? <><LockOpen className="h-3.5 w-3.5 mr-1.5" />Unlock crew access</> : <><Lock className="h-3.5 w-3.5 mr-1.5" />Lock all crew access</>}
                </Button>
              </div>
            )}

            {/* ───── Active Crew section ───── */}
            <div className="mt-8" data-testid="section-crew">
              <div className="flex items-center gap-2 mb-2">
                <UsersIcon className="h-4 w-4 text-zinc-500" />
                <h2 className="font-display text-lg">Active Crew</h2>
                <span className="text-xs text-zinc-400">({crewRows.length})</span>
              </div>
              <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="member-list">
                {crewRows.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    <UsersIcon className="h-6 w-6 mx-auto text-zinc-300" />
                    <div className="mt-2 text-sm">No crew yet on this yacht.</div>
                  </div>
                ) : (
                  <ul className="divide-y divide-zinc-200">
                    {crewRows.map(renderRow)}
                  </ul>
                )}
              </div>
            </div>

            {/* ───── Ex-Crew section (HR retained, re-invitable) ───── */}
            {SENIOR.includes(myRole) && exCrew.length > 0 && (
              <div className="mt-8" data-testid="section-ex-crew">
                <div className="flex items-center gap-2 mb-2">
                  <UsersIcon className="h-4 w-4 text-zinc-400" />
                  <h2 className="font-display text-lg text-zinc-600">Ex-Crew</h2>
                  <span className="text-xs text-zinc-400">({exCrew.length})</span>
                </div>
                <p className="text-xs text-zinc-500 mb-2">
                  Crew removed from this yacht. Their HR records (employment, payroll &amp; documents) are kept here. Re-invite them to bring them back as Active Crew with all data intact.
                </p>
                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="ex-crew-list">
                  <ul className="divide-y divide-zinc-200">
                    {exCrew.map((x) => (
                      <li key={x.user_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3" data-testid={`ex-crew-row-${x.user_id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-zinc-100 grid place-items-center text-xs font-medium text-zinc-500 shrink-0">
                            {(x.name || x.email || "U").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{x.name || x.email}</div>
                            <div className="text-xs text-zinc-500 truncate">{x.email}</div>
                            <div className="text-[11px] text-zinc-400 mt-0.5">
                              {x.has_employment && <span className="mr-2">Employment ✓</span>}
                              {x.paylog_count > 0 && <span className="mr-2">{x.paylog_count} payroll {x.paylog_count === 1 ? "entry" : "entries"}</span>}
                              {x.document_count > 0 && <span>{x.document_count} document{x.document_count === 1 ? "" : "s"}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap pl-12 sm:pl-0 shrink-0">
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-zinc-200 text-zinc-500">
                            {ROLE_LABELS[x.last_role] || x.last_role}
                          </span>
                          <Link
                            to={`/app/crew-hr/${x.user_id}?yacht_id=${activeYacht.id}`}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                            data-testid={`ex-hr-link-${x.user_id}`}
                          >
                            <IdCard className="h-3 w-3" /> HR record
                          </Link>
                          <Button
                            size="sm"
                            className="bg-ink hover:bg-black h-7"
                            onClick={() => reinviteExCrew(x)}
                            data-testid={`ex-reinvite-${x.user_id}`}
                          >
                            <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Re-invite
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setToPurge(x)}
                            data-testid={`ex-purge-${x.user_id}`}
                            title="Permanently delete their HR data for this yacht"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* ───── Service providers section ───── */}
            <div className="mt-8" data-testid="section-providers">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-4 w-4 text-zinc-500" />
                <h2 className="font-display text-lg">Online Suppliers</h2>
                <span className="text-xs text-zinc-400">({providerRows.length})</span>
              </div>
              <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="provider-list">
                {providerRows.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    <Briefcase className="h-6 w-6 mx-auto text-zinc-300" />
                    <div className="mt-2 text-sm">No online suppliers on this yacht yet.</div>
                  </div>
                ) : (
                  <ul className="divide-y divide-zinc-200">
                    {providerRows.map(renderRow)}
                  </ul>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* Lock-member confirmation */}
      <Dialog open={!!toLock} onOpenChange={(o) => { if (!o && !lockBusy) setToLock(null); }}>
        <DialogContent data-testid="lock-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Lock access for {toLock?.name || toLock?.email}?</DialogTitle>
            <DialogDescription>
              This yacht will disappear from their account immediately — they keep their login but
              can't see or touch anything here until you unlock them. They'll be notified by email
              and in-app. This is fully reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToLock(null)} disabled={lockBusy}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => setMemberLock(toLock, true)}
              disabled={lockBusy}
              data-testid="lock-confirm-btn"
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />{lockBusy ? "Locking…" : "Lock access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crew-lock (all crew) confirmation */}
      <Dialog open={!!crewLockConfirm} onOpenChange={(o) => { if (!o && !lockBusy) setCrewLockConfirm(null); }}>
        <DialogContent data-testid="crew-lock-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {crewLockConfirm?.enable ? "Lock ALL crew access?" : "Restore crew access?"}
            </DialogTitle>
            <DialogDescription>
              {crewLockConfirm?.enable
                ? "Everyone below Manager — including the Captain — instantly loses access to this yacht. Owners, Managers and linked suppliers are not affected. Every locked member is notified by email and in-app. You can undo this at any time."
                : "Everyone locked by the crew switch gets their access back and is notified. Individually locked members stay locked until unlocked from their row."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrewLockConfirm(null)} disabled={lockBusy}>Cancel</Button>
            <Button
              variant={crewLockConfirm?.enable ? "destructive" : "default"}
              className={crewLockConfirm?.enable ? "" : "bg-ink hover:bg-black"}
              onClick={() => applyCrewLock(!!crewLockConfirm?.enable)}
              disabled={lockBusy}
              data-testid="crew-lock-confirm-btn"
            >
              {lockBusy ? "Applying…" : crewLockConfirm?.enable ? "Lock all crew" : "Restore access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove-crew confirmation */}
      <Dialog open={!!toRemove} onOpenChange={(o) => { if (!o) setToRemove(null); }}>
        <DialogContent data-testid="remove-confirm-dialog">
          {(() => {
            const isSelf = toRemove?.user_id === user?.id;
            const isPendingInvite = !!toRemove?.pending && !isSelf;
            const needsApproval = !isPendingInvite && (isSelf || (toRemove && ranksAbove(toRemove)));
            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {isPendingInvite ? "Cancel this pending invite?"
                      : isSelf ? "Leave this yacht?"
                        : needsApproval ? "Request removal?"
                          : "Remove this person from the crew?"}
                  </DialogTitle>
                  <DialogDescription>
                    {isPendingInvite ? (
                      <>
                        <span className="font-medium text-ink">{toRemove?.name || toRemove?.email}</span>
                        {" "}hasn't confirmed their account yet, so this is just an open invitation. Cancelling
                        will remove the placeholder and their invite link will stop working. No approval needed.
                      </>
                    ) : isSelf ? (
                      <>
                        You're about to leave the crew of <span className="font-medium">{activeYacht.name}</span>.
                        Because someone senior to you is on this yacht, Seavanto will email them a link to
                        approve your departure. Your account stays active and you remain in everyone's network.
                      </>
                    ) : (
                      <>
                        You're about to {needsApproval ? "request the removal of " : "remove "}
                        <span className="font-medium text-ink">{toRemove?.name || toRemove?.email}</span>
                        {toRemove?.role && (
                          <> (<span className="lowercase">{ROLE_LABELS[toRemove.role] || toRemove.role}</span>)</>
                        )}{" "}
                        from <span className="font-medium">{activeYacht.name}</span>.
                        {needsApproval ? (
                          <>
                            {" "}Because their role is ranked higher than yours, Seavanto will email them a
                            confirmation link. They must approve the request before they're removed. Their
                            Seavanto account stays intact and they'll remain in your network either way.
                          </>
                        ) : (
                          <> They'll lose access to this yacht only — their Seavanto account, network membership and other yacht assignments stay intact.</>
                        )}
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setToRemove(null)} data-testid="remove-cancel">
                    {isPendingInvite ? "Keep the invite"
                      : isSelf ? "Stay on the crew"
                        : "No, keep them"}
                  </Button>
                  <Button
                    onClick={confirmRemove}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="remove-confirm"
                  >
                    {isPendingInvite ? "Yes, cancel invite"
                      : isSelf ? "Request to leave"
                        : needsApproval ? "Send removal request"
                          : "Yes, remove"}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Permanently delete ex-crew HR data confirmation */}
      <Dialog open={!!toPurge} onOpenChange={(o) => { if (!o) setToPurge(null); }}>
        <DialogContent data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete HR data?</DialogTitle>
            <DialogDescription>
              This permanently deletes all retained HR data for{" "}
              <span className="font-medium text-ink">{toPurge?.name || toPurge?.email}</span>{" "}
              on <span className="font-medium">{activeYacht?.name}</span> — employment terms,
              payroll history, bank details, the profile copy and any documents.
              This cannot be undone. Their Seavanto account and other yachts are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToPurge(null)} data-testid="purge-cancel">
              Cancel
            </Button>
            <Button
              onClick={purgeExCrewHr}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-confirm"
            >
              Yes, delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
